import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

export const useGlobalTrackStyles = createUseStyles((theme: ThemeType) => ({
  page: {
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.color.semantic.background.neutral.primary,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center bottom',
    fontFamily: theme.typography.fontFamily.primary,
  },
  content: {
    flex: 1,
    width: '100%',
    maxWidth: '1200px',
    margin: `${theme.spacing[0]} auto`,
    padding: theme.spacing[6],
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing[5],
  },
  loaderWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing[8],
  },
  pageLoader: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyWrapper: {
    backgroundColor: theme.color.semantic.background.neutral.secondary,
    borderRadius: theme.radius.md,
    padding: theme.spacing[8],
  },
  captchaWidget: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 1000,
  },
}));

export const useTrackHeaderStyles = createUseStyles((theme: ThemeType) => ({
  header: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing[3],
    padding: `${theme.spacing[4]} ${theme.spacing[6]}`,
  },
  logoWrap: {
    display: 'flex',
    alignItems: 'center',
  },
  logo: {
    maxHeight: '48px',
    maxWidth: '200px',
    objectFit: 'contain',
  },
  languageSelect: {
    minWidth: '150px',
  },
  orgName: {
    fontSize: theme.typography.fontSize.lg,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.color.semantic.text.primary,
  },
}));

export const useSearchBoxStyles = createUseStyles((theme: ThemeType) => ({
  card: {
    width: '100%',
    '& .ant-card-head-title': {
      color: theme.color.semantic.brand.default.primary,
      fontSize: theme.typography.fontSize.xl,
      fontWeight: theme.typography.fontWeight.semibold,
    },
  },
  field: {
    marginBottom: theme.spacing[4],
  },
  label: {
    display: 'block',
    marginBottom: theme.spacing[2],
    '&.ant-typography': {
      color: theme.color.semantic.text.primary,
      fontWeight: `${theme.typography.fontWeight.semibold} !important`,
    },
  },
  radioGroup: {
    marginBottom: theme.spacing[5],
  },
  radioRow: {
    width: '100%',
  },
  select: {
    width: '100%',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing[5],
  },
  poweredBy: {
    color: theme.color.semantic.text.tertiary,
    '& .ant-image': {
      display: 'flex',
      alignItems: 'center',
    },
    '& .ant-image img': {
      display: 'block',
    },
  },
}));

export const useTrackResultsStyles = createUseStyles((theme: ThemeType) => ({
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing[4],
  },
  card: {
    width: '100%',
  },
  tagRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing[2],
    marginBottom: theme.spacing[4],
  },
  statusTag: {
    '&.ant-tag': {
      minWidth: '140px',
      textAlign: 'center',
      padding: `${theme.spacing[1]} ${theme.spacing[4]}`,
      backgroundColor: theme.color.semantic.accent.green.strong,
      color: theme.color.semantic.text.inverse,
      border: 'none',
    },
  },
  fieldRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: `${theme.spacing[2]} ${theme.spacing[6]}`,
  },
  field: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing[2],
  },
  fieldKey: {
    '&.ant-typography': {
      color: theme.color.semantic.text.tertiary,
      fontWeight: theme.typography.fontWeight.medium,
    },
  },
  fieldValue: {
    '&.ant-typography': {
      color: theme.color.semantic.text.primary,
      fontWeight: theme.typography.fontWeight.semibold,
      wordBreak: 'break-word',
    },
  },
  divider: {
    marginTop: theme.spacing[4],
    marginBottom: theme.spacing[4],
  },
  detailsRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing[2],
  },
  detailsLink: {
    paddingRight: theme.spacing[0],
  },
}));

const TIMELINE_AXIS_OFFSET = '130px';

export const useTrackingTimelineStyles = createUseStyles((theme: ThemeType) => ({
  timeline: {
    marginTop: theme.spacing[2],
    paddingTop: theme.spacing[5],
    // antd's label mode centers the axis (50/50). These overrides (scoped with the extra JSS
    // class + .ant-timeline-label to out-specify antd's own rules) pull the axis into a narrow
    // left date-column with the content flowing right beside the rail, matching the source page.
    '&.ant-timeline.ant-timeline-label .ant-timeline-item-label': {
      width: '104px',
      textAlign: 'right',
    },
    '&.ant-timeline.ant-timeline-label .ant-timeline-item-tail, &.ant-timeline.ant-timeline-label .ant-timeline-item-head, &.ant-timeline.ant-timeline-label .ant-timeline-item-head-custom':
      {
        insetInlineStart: TIMELINE_AXIS_OFFSET,
        left: TIMELINE_AXIS_OFFSET,
      },
    '&.ant-timeline.ant-timeline-label .ant-timeline-item .ant-timeline-item-content': {
      insetInlineStart: '164px',
      left: '164px',
      width: 'calc(100% - 180px)',
      marginInlineStart: theme.spacing[0],
      marginLeft: theme.spacing[0],
      textAlign: 'left',
    },
    '& .ant-timeline-item-head-custom': {
      width: 'auto !important',
      height: 'auto !important',
      padding: `${theme.spacing[0]} !important`,
      backgroundColor: 'transparent !important',
      border: 'none !important',
    },
    '& .ant-timeline-item-tail': {
      borderInlineStartWidth: theme.border.width.md,
    },
  },
  dot: {
    width: theme.spacing[6],
    height: theme.spacing[6],
    borderRadius: theme.radius.full,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.color.semantic.text.inverse,
    fontSize: theme.typography.fontSize.xs,
  },
  dotDone: {
    backgroundColor: theme.color.semantic.accent.green.strong,
  },
  dotTransit: {
    backgroundColor: theme.color.semantic.accent.blue.strong,
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
  },
  date: {
    '&.ant-typography': {
      color: theme.color.semantic.text.primary,
      fontSize: theme.typography.fontSize.sm,
    },
  },
  time: {
    '&.ant-typography': {
      color: theme.color.semantic.text.tertiary,
      fontSize: theme.typography.fontSize.xs,
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing[1],
    paddingBottom: theme.spacing[3],
  },
  eventTitle: {
    '&.ant-typography': {
      color: theme.color.semantic.text.primary,
      fontWeight: theme.typography.fontWeight.semibold,
    },
  },
  groupItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing[1],
    marginTop: theme.spacing[2],
  },
  receiverInfo: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing[4],
    marginTop: theme.spacing[1],
  },
}));
